import {HttpClient, HttpContext} from '@angular/common/http';
import {Injectable, inject} from '@angular/core';
import {GateweeTableColumn} from '@gatewee/interfaces';
import {Filter} from '@gatewee/types/filter';
import {resolveWalletLabel, Wallet} from '@gatewee/types/wallet';
import {WalletLabelPipe} from '@gatewee/utils/pipes/wallet-label.pipe';
import {ProviderPipe} from '@gatewee/utils/pipes/provider.pipe';
import {TranslateService} from '@ngx-translate/core';
import {Observable, catchError, of, tap} from 'rxjs';
import {SKIP_SPINNER} from './tokens/spinner.token';

const walletFilterKey = 'options.where.wallet.id';

@Injectable({
  providedIn: 'root',
})
export class WalletTableSupport {
  private readonly http = inject(HttpClient);
  private readonly providerPipe = inject(ProviderPipe);

  wallets: Wallet[] = [];
  hasMultipleWallets = false;

  loadRecipientWallets(): Observable<Wallet[]> {
    return this.http
      .get<Wallet[]>('/wallets/recipient', {
        context: new HttpContext().set(SKIP_SPINNER, true),
      })
      .pipe(
        tap(wallets => this.setWallets(wallets)),
        catchError(() => {
          this.setWallets([]);
          return of([]);
        }),
      );
  }

  loadDomainWalletsForRecipient(
    recipientId: number | null | undefined,
  ): Observable<Wallet[]> {
    if (!recipientId) {
      this.setWallets([]);
      return of([]);
    }

    return this.http
      .get<Wallet[]>(`/wallets/domain/recipient/${recipientId}`, {
        context: new HttpContext().set(SKIP_SPINNER, true),
      })
      .pipe(
        tap(wallets => this.setWallets(wallets)),
        catchError(() => {
          this.setWallets([]);
          return of([]);
        }),
      );
  }

  extractRecipientIdFromFilters(
    activeFilters: Record<string, unknown>,
  ): number | null {
    const recipientRaw = activeFilters['options.where.recipient.id'];

    if (
      recipientRaw === '' ||
      recipientRaw === null ||
      recipientRaw === undefined
    ) {
      return null;
    }

    const id = Number(recipientRaw);
    return Number.isFinite(id) ? id : null;
  }

  syncWalletFilter(filters: Filter[], translate: TranslateService): Filter[] {
    const withoutWallet = filters.filter(
      filter => filter.key !== walletFilterKey,
    );
    const walletFilter = this.buildWalletFilter(translate);
    if (!walletFilter) {
      return withoutWallet;
    }

    const statusIndex = withoutWallet.findIndex(filter =>
      filter.key.includes('.status'),
    );
    const insertAt = statusIndex >= 0 ? statusIndex + 1 : withoutWallet.length;
    withoutWallet.splice(insertAt, 0, walletFilter);
    return withoutWallet;
  }

  buildWalletColumn(
    walletLabelPipe: WalletLabelPipe,
  ): GateweeTableColumn | null {
    if (!this.hasMultipleWallets) {
      return null;
    }

    return {
      label: 'common.table.columns.wallet',
      field: 'wallet',
      sortable: true,
      visible: true,
      class: 'gw-text-text-primary gw-text-nowrap',
      pipe: {
        fn: walletLabelPipe,
        args: [],
      },
    };
  }

  insertWalletColumn(
    columns: GateweeTableColumn[],
    walletColumn: GateweeTableColumn | null,
    beforeField = 'status',
  ): GateweeTableColumn[] {
    if (!walletColumn) {
      return columns;
    }

    const next = [...columns];
    const beforeIndex = next.findIndex(column => column.field === beforeField);
    const actionsIndex = next.findIndex(column => column.field === 'actions');
    const insertAt =
      beforeIndex >= 0
        ? beforeIndex
        : actionsIndex >= 0
        ? actionsIndex
        : next.length;
    next.splice(insertAt, 0, walletColumn);
    return next;
  }

  walletListRelations(): Record<string, boolean> {
    return {wallet: true};
  }

  private buildWalletFilter(translate: TranslateService): Filter | null {
    if (!this.hasMultipleWallets) {
      return null;
    }

    return {
      key: walletFilterKey,
      label: translate.instant('common.filters.wallet'),
      type: 'select',
      value: '',
      data: [
        {label: translate.instant('common.filters.all'), value: ''},
        ...this.wallets.map(wallet => ({
          label: resolveWalletLabel(wallet, {
            providerLabel: provider => this.providerPipe.transform(provider),
            fallbackLabel: id =>
              translate.instant('common.table.walletFallback', {id}),
          }),
          value: wallet.id,
        })),
      ],
    };
  }

  private setWallets(wallets: Wallet[] | null | undefined): void {
    this.wallets = wallets ?? [];
    this.hasMultipleWallets = this.wallets.length > 1;
  }
}
